<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Input, Select, Textarea } from '@drama/ui'
import FormField from '../NovelFormField.vue'
import { newCue, newCueTrigger } from '@drama/novel-authoring'
import { numberValue } from '@drama/novel-authoring'
import { useSectionModel } from './section-model.js'
import './novel-sections.css'

const TRIGGER_KINDS = ['topic', 'entity_mentioned']
/** Select 只收字符串值：用空串代表"不适用"，写回时换成 null（话题触发的 entity_id 必须是 null）。 */
const NOT_APPLICABLE = ''

const props = defineProps({
  /** 整本作品。 */
  modelValue: { type: Object, required: true },
  issues: { type: Array, default: () => [] },
  /** 人物在 characters 里的下标。 */
  index: { type: Number, required: true },
})

const emit = defineEmits(['update:modelValue'])
const { t } = useI18n()
const { bind, invalid, removeAt, toggle, write, error } = useSectionModel(props, emit)
const person = computed(() => props.modelValue.characters[props.index])
const cues = computed(() => person.value.performance_cues ?? [])
const triggerKindOptions = computed(() => TRIGGER_KINDS.map(kind => ({ value: kind, label: t(`novelStudio.triggerKinds.${kind}`) })))
const entityOptions = computed(() => [
  { value: NOT_APPLICABLE, label: t('novelStudio.fields.notApplicable') },
  ...[...(props.modelValue.characters ?? []), ...(props.modelValue.locations ?? []), ...(props.modelValue.items ?? [])]
    .map(entity => ({ value: entity.id, label: `${entity.name || entity.id} · ${entity.id}` })),
])
/** 演出线索只能引用这个人物知道的事实。 */
const knownFacts = computed(() => (props.modelValue.facts ?? []).filter(fact => (fact.observer_ids ?? []).includes(person.value.id)))
const knownFactIds = computed(() => knownFacts.value.map(fact => fact.id))

function cuePath(cueIndex, ...rest) {
  return ['characters', props.index, 'performance_cues', cueIndex, ...rest]
}

function cueName(cueIndex) {
  return `${t('novelStudio.fields.performanceCues')} #${cueIndex + 1}`
}

function addCue() {
  write(['characters', props.index, 'performance_cues'], [...cues.value, newCue()])
}

/** 触发条件与冷却轮数成对出现：加触发条件时一并写入冷却 1 轮。 */
function addTrigger(cueIndex) {
  write(cuePath(cueIndex), { ...cues.value[cueIndex], ...newCueTrigger() })
}

/** 触发条件与冷却轮数成对删除，没有触发条件的线索不能带冷却轮数。 */
function removeTrigger(cueIndex) {
  const cue = { ...cues.value[cueIndex] }
  delete cue.trigger
  delete cue.cooldown_turns
  write(cuePath(cueIndex), cue)
}

/** 话题触发不关联实体；改成实体提及时补上第一个可选实体（人物、地点、物品依次），与 Demo 一致。 */
function setTriggerKind(cueIndex, kind) {
  const trigger = { ...cues.value[cueIndex].trigger, kind }
  const pack = props.modelValue
  if (kind === 'topic') trigger.entity_id = null
  else if (!trigger.entity_id) trigger.entity_id = pack.characters?.[0]?.id || pack.locations?.[0]?.id || pack.items?.[0]?.id || ''
  write(cuePath(cueIndex, 'trigger'), trigger)
}

function addTerm(cueIndex) {
  write(cuePath(cueIndex, 'trigger', 'terms'), [...(cues.value[cueIndex].trigger.terms ?? []), ''])
}
</script>

<template>
  <section
    class="novel-entity__nested"
    :data-path="`characters.${index}.performance_cues`"
    :data-testid="`novel-character-${index}-cues`"
  >
    <div class="novel-entity__nested-header">
      <h4>{{ t('novelStudio.requirements.optionalLabel', { label: t('novelStudio.fields.performanceCues') }) }}</h4>
      <Button
        variant="secondary"
        size="sm"
        :data-testid="`novel-character-${index}-add-cue`"
        @click="addCue"
      >
        {{ t('novelStudio.fields.addEntry') }}
      </Button>
    </div>
    <div
      v-for="(cue, cueIndex) in cues"
      :key="cueIndex"
      class="novel-entity__sub"
      :class="{ 'has-error': invalid(cuePath(cueIndex)) }"
      :data-path="cuePath(cueIndex).join('.')"
      tabindex="-1"
      :data-testid="`novel-character-${index}-cue-${cueIndex}`"
    >
      <FormField
        :error="error(cuePath(cueIndex, 'text'))"
        required
        :label="t('novelStudio.fields.cueText')"
      >
        <Textarea
          v-bind="bind(cuePath(cueIndex, 'text'))"
          :rows="2"
        />
      </FormField>
      <fieldset
        class="novel-entity__checks"
        :data-path="cuePath(cueIndex, 'fact_ids').join('.')"
      >
        <legend>{{ t('novelStudio.requirements.optionalLabel', { label: t('novelStudio.fields.factScope') }) }}</legend>
        <p
          v-if="!knownFacts.length"
          class="novel-entities__hint"
        >
          {{ t('novelStudio.empty.cueFacts') }}
        </p>
        <label
          v-for="fact in knownFacts"
          :key="fact.id"
          class="novel-entity__check"
        >
          <input
            type="checkbox"
            :checked="(cue.fact_ids || []).includes(fact.id)"
            @change="toggle(cuePath(cueIndex, 'fact_ids'), fact.id, $event.target.checked, knownFactIds)"
          >
          {{ fact.id }} · {{ (fact.text || '').slice(0, 60) }}
        </label>
      </fieldset>
      <div
        v-if="cue.trigger"
        class="novel-entity__nested"
      >
        <div class="novel-entity__nested-header">
          <h4>{{ t('novelStudio.requirements.optionalLabel', { label: t('novelStudio.fields.trigger') }) }}</h4>
          <Button
            variant="secondary"
            size="sm"
            @click="removeTrigger(cueIndex)"
          >
            {{ t('novelStudio.fields.removeTrigger') }}
          </Button>
        </div>
        <div class="novel-entity__grid">
          <FormField
            :error="error(cuePath(cueIndex, 'trigger', 'kind'))"
            required
            :label="t('novelStudio.fields.triggerKind')"
          >
            <Select
              :model-value="cue.trigger.kind ?? ''"
              :options="triggerKindOptions"
              :aria-label="t('novelStudio.fields.triggerKind')"
              :invalid="invalid(cuePath(cueIndex, 'trigger', 'kind'))"
              :data-path="cuePath(cueIndex, 'trigger', 'kind').join('.')"
              block
              @update:model-value="setTriggerKind(cueIndex, $event)"
            />
          </FormField>
          <FormField
            :error="error(cuePath(cueIndex, 'trigger', 'entity_id'))"
            :required="cue.trigger.kind !== 'topic'"
            :not-applicable="cue.trigger.kind === 'topic'"
            :label="t('novelStudio.fields.triggerEntity')"
          >
            <Select
              :model-value="cue.trigger.entity_id ?? NOT_APPLICABLE"
              :options="entityOptions"
              :aria-label="t('novelStudio.fields.triggerEntity')"
              :disabled="cue.trigger.kind === 'topic'"
              :invalid="invalid(cuePath(cueIndex, 'trigger', 'entity_id'))"
              :data-path="cuePath(cueIndex, 'trigger', 'entity_id').join('.')"
              block
              @update:model-value="write(cuePath(cueIndex, 'trigger', 'entity_id'), $event === NOT_APPLICABLE ? null : $event)"
            />
          </FormField>
          <FormField
            :error="error(cuePath(cueIndex, 'cooldown_turns'))"
            required
            :label="t('novelStudio.fields.cooldown')"
          >
            <Input
              type="number"
              min="1"
              max="12"
              :model-value="cue.cooldown_turns ?? ''"
              :invalid="invalid(cuePath(cueIndex, 'cooldown_turns'))"
              :data-path="cuePath(cueIndex, 'cooldown_turns').join('.')"
              @update:model-value="write(cuePath(cueIndex, 'cooldown_turns'), numberValue($event))"
            />
          </FormField>
        </div>
        <div
          class="novel-entity__nested"
          :data-path="cuePath(cueIndex, 'trigger', 'terms').join('.')"
        >
          <div class="novel-entity__nested-header">
            <h4>{{ t('novelStudio.requirements.requiredLabel', { label: t('novelStudio.fields.triggerTerms') }) }}</h4>
            <Button
              variant="secondary"
              size="sm"
              @click="addTerm(cueIndex)"
            >
              {{ t('novelStudio.fields.addEntry') }}
            </Button>
          </div>
          <div
            v-for="(term, termIndex) in cue.trigger.terms || []"
            :key="termIndex"
            class="novel-entity__inline"
          >
            <Input
              v-bind="bind(cuePath(cueIndex, 'trigger', 'terms', termIndex))"
              :aria-label="t('novelStudio.fields.triggerTerms')"
            />
            <Button
              variant="secondary"
              size="sm"
              :aria-label="t('novelStudio.fields.removeNamed', { name: term || `${t('novelStudio.fields.triggerTerms')} #${termIndex + 1}` })"
              @click="removeAt(cuePath(cueIndex, 'trigger', 'terms', termIndex))"
            >
              ×
            </Button>
          </div>
        </div>
      </div>
      <div class="novel-entity__actions">
        <Button
          v-if="!cue.trigger"
          variant="secondary"
          size="sm"
          @click="addTrigger(cueIndex)"
        >
          {{ t('novelStudio.fields.addTrigger') }}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          :aria-label="t('novelStudio.fields.removeNamed', { name: cueName(cueIndex) })"
          @click="removeAt(cuePath(cueIndex))"
        >
          {{ t('novelStudio.fields.remove') }}
        </Button>
      </div>
    </div>
  </section>
</template>
